import { useEffect, useState, type ComponentType } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDocumentTitle } from '@/lib/seo'
import { useLocale } from '@/i18n/useLocale'
import { localePath, ROUTES } from '@/lib/routes'
import { getPost } from '@/lib/news'
import { NewsletterSignup } from '@/components/NewsletterSignup'

export default function NewsPost() {
  const { slug } = useParams()
  const { t } = useTranslation()
  const locale = useLocale()
  const post = slug ? getPost(slug) : undefined
  useDocumentTitle(post ? `${post.title} — ${t('site.brandLong')}` : t('notFound.title'))

  const [Body, setBody] = useState<ComponentType | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let active = true
    if (!post) return
    void post.Component().then((m) => {
      if (active) setBody(() => m.default)
    })
    return () => {
      active = false
    }
  }, [post])

  if (!post) {
    return (
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-24 text-center">
        <h1 className="text-3xl font-bold">{t('notFound.title')}</h1>
        <Link to={localePath(locale, ROUTES.news)} className="inline-block mt-6 underline opacity-80">
          {t('newsPost.back')}
        </Link>
      </section>
    )
  }

  const onShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* noop */
    }
  }

  // Phase 2 placeholder posts — fixed reading time. Reading time will be derived
  // from real body length once raw MDX text is exposed (Phase 2.5 follow-up).
  const minutes = 1

  return (
    <article className="max-w-3xl mx-auto px-5 md:px-8 py-16">
      <header>
        <div className="text-base uppercase tracking-wider opacity-60">{post.tag}</div>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold font-display">{post.title}</h1>
        <div className="mt-2 text-base opacity-60">
          {post.date} · {t('newsPost.readingTime', { minutes })}
        </div>
        {post.author && (
          <div className="mt-1 text-base opacity-60">
            {t('newsPost.author', { name: post.author })}
          </div>
        )}
      </header>

      <div className="prose prose-sm md:prose-base prose-invert dark:prose-invert mt-8 max-w-none">
        {Body ? <Body /> : null}
      </div>

      <footer className="mt-12 pt-6 border-t border-mono-300 dark:border-mono-800 flex flex-wrap items-center gap-4 text-base">
        <Link to={localePath(locale, ROUTES.news)} className="underline opacity-80">
          {t('newsPost.back')}
        </Link>
        <button type="button" onClick={onShare} className="underline opacity-80">
          {copied ? t('newsPost.shared') : t('newsPost.share')}
        </button>
      </footer>

      <div className="mt-10">
        <NewsletterSignup />
      </div>
    </article>
  )
}
